/* ===== Vendor — lazy third-party libraries =====
 *
 *   const XLSX  = await Vendor.load('xlsx');
 *   const JSZip = await Vendor.load('jszip');
 *   const pdfjs = await Vendor.load('pdfjs');       // + 'pdfjsWorker', see below
 *
 * Injects the script once (shared promise; concurrent callers wait on the
 * same tag), resolves with the library's global, rejects — and forgets the
 * attempt so a retry is possible — when the tag fails to load. If the
 * global is already present (the classic eager <script> tags, until I1-S1
 * removes them) it resolves immediately.
 *
 * Sources: the exact cdnjs URLs + SRI hashes orderly/index.html uses today
 * (computed 2026-09-04, see the EP-08 comment there). Vendoring them under
 * js/vendor/ is an owner decision for I1-A; only this table changes then.
 *
 * pdf.js (the Allocations screen's PDF upload, 2026-09-15) is TWO entries and
 * both are needed:
 *
 *   await Promise.all([Vendor.load('pdfjs'), Vendor.load('pdfjsWorker')]);
 *   const doc = await globalThis.pdfjsLib.getDocument({ data, isEvalSupported: false }).promise;
 *
 * pdf.js normally parses in a Web Worker it constructs from
 * GlobalWorkerOptions.workerSrc; for a CROSS-ORIGIN workerSrc it wraps the
 * URL in a blob: worker. orderly/_headers sets no worker-src, so workers
 * fall back to child-src → default-src 'self': neither the cdnjs URL nor a
 * blob: URL is allowed, and the construction would be a CSP violation (pdf.js
 * catches the throw, but the report is real and the console error looks like
 * a bug). So workerSrc is NEVER set. Loading pdf.worker.min.js as an ordinary
 * script instead — script-src already allows cdnjs, and this table pins its
 * SRI — puts `globalThis.pdfjsWorker.WorkerMessageHandler` in place, which is
 * exactly what PDFWorker._initialize() checks before it tries to construct a
 * Worker: with it present the real-worker path is skipped and the document is
 * parsed on the main thread. Reading a supplier pre-order (a few pages of
 * text, getTextContent only — nothing is rendered) is milliseconds of work.
 *
 * URLs verified 200 and hashed from the served bytes on 2026-09-15:
 *   curl -sL <url> | openssl dgst -sha384 -binary | openssl base64 -A
 */

export const VENDORS = Object.freeze({
  xlsx: Object.freeze({
    global: 'XLSX',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js',
    integrity: 'sha384-vtjasyidUo0kW94K5MXDXntzOJpQgBKXmE7e2Ga4LG0skTTLeBi97eFAXsqewJjw',
  }),
  jszip: Object.freeze({
    global: 'JSZip',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js',
    integrity: 'sha384-+mbV2IY1Zk/X1p/nWllGySJSUN8uMs+gUAN10Or95UBH0fpj6GfKgPmgC5EXieXG',
  }),
  // pdf.js 3.11.174 legacy UMD build (global pdfjsLib) — 313 KB, loaded only
  // when a PDF is dropped on the Allocations screen.
  pdfjs: Object.freeze({
    global: 'pdfjsLib',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
    integrity: 'sha384-/1qUCSGwTur9vjf/z9lmu/eCUYbpOTgSjmpbMQZ1/CtX2v/WcAIKqRv+U1DUCG6e',
  }),
  // the matching worker build (global pdfjsWorker) — 1.0 MB, loaded on the
  // main thread ON PURPOSE (the CSP has no worker-src; see the header).
  pdfjsWorker: Object.freeze({
    global: 'pdfjsWorker',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
    integrity: 'sha384-SnzOobpRMLXZ52iJvZm/C0fYw0OQemTXzTjIsdsfMcrCtCEe9qgzxTd3RSklO5x2',
  }),
});

const pending = new Map();

function existingTag(doc, spec) {
  const tags = doc.querySelectorAll('script[src]');
  for (const t of tags) {
    if (t.src === spec.src || t.getAttribute('src') === spec.src) return t;
  }
  return null;
}

function waitForGlobal(name, ms = 8000) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (globalThis[name]) return resolve(globalThis[name]);
      if (Date.now() - started > ms) return reject(new Error(`Vendor: ${name} did not become available`));
      setTimeout(tick, 50);
    };
    tick();
  });
}

export const Vendor = {
  registry: VENDORS,

  isLoaded(name) {
    const spec = VENDORS[name];
    return !!(spec && globalThis[spec.global]);
  },

  /** @returns {Promise<any>} the library global (XLSX, JSZip) */
  load(name) {
    const spec = VENDORS[name];
    if (!spec) return Promise.reject(new Error(`Vendor: unknown library "${name}"`));
    if (globalThis[spec.global]) return Promise.resolve(globalThis[spec.global]);
    if (pending.has(name)) return pending.get(name);

    const doc = globalThis.document;
    if (!doc) return Promise.reject(new Error('Vendor: no document'));

    const p = new Promise((resolve, reject) => {
      const done = () => waitForGlobal(spec.global).then(resolve, reject);
      const fail = (err) => reject(new Error(`Vendor: failed to load ${name} from ${spec.src}${err?.message ? ' — ' + err.message : ''}`));
      const tag = existingTag(doc, spec);
      if (tag) {
        // A classic eager tag is present but not executed yet (defer): wait for it.
        tag.addEventListener('load', done, { once: true });
        tag.addEventListener('error', fail, { once: true });
        waitForGlobal(spec.global, 15000).then(resolve, () => { /* load/error decides */ });
        return;
      }
      const s = doc.createElement('script');
      s.src = spec.src;
      s.async = true;
      s.crossOrigin = 'anonymous';
      if (spec.integrity) s.integrity = spec.integrity;
      s.dataset.vendor = name;
      s.addEventListener('load', done, { once: true });
      s.addEventListener('error', (e) => { s.remove(); fail(e); }, { once: true });
      (doc.head || doc.documentElement).appendChild(s);
    });
    pending.set(name, p);
    p.catch(() => pending.delete(name));
    return p;
  },

  /** Warm a library in the background (e.g. on hover over "Download Excel"). */
  prefetch(name) {
    return this.load(name).catch(() => null);
  },
};
